import { CheckCircle2, FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import {
  type SettlementStatement,
  useCreateSettlement,
  useReconcileSettlement,
  useSettlements,
} from '@/hooks/use-settlements';

function fmt(v: string | number): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(n);
}

function fmtDate(d?: string): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ISSUED: 'bg-blue-100 text-blue-700',
  RECONCILED: 'bg-green-100 text-green-700',
  DISPUTED: 'bg-red-100 text-red-700',
};

interface Props {
  loanId: string;
  loan?: any;
}

export function SettlementTab({ loanId, loan }: Props) {
  const { data: settlements, isLoading } = useSettlements(loanId);
  const createSettlement = useCreateSettlement(loanId);
  const reconcile = useReconcileSettlement(loanId);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    loanAdvance: loan?.principal || '',
    establishmentFee: loan?.establishmentFee || '',
    brokerageFee: loan?.brokerageFee || '',
    legalCosts: loan?.legalCosts || '',
    titleInsurance: '',
    valuationFee: '',
    stampDuty: '',
    otherDeductions: '',
    otherDeductionsDesc: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" /> Loading settlements…
      </div>
    );
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};

    const validateRequiredPositive = (key: string, value: string | number | undefined) => {
      const raw = value === undefined || value === null ? '' : String(value);
      if (!raw.trim()) {
        nextErrors[key] = 'This field is required.';
        return;
      }
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) {
        nextErrors[key] = 'Enter a valid number.';
        return;
      }
      if (parsed <= 0) {
        nextErrors[key] = 'Value must be greater than 0.';
      }
    };

    const validateOptionalNonNegative = (key: string, value: string | number | undefined) => {
      const raw = value === undefined || value === null ? '' : String(value);
      if (!raw.trim()) return;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed)) {
        nextErrors[key] = 'Enter a valid number.';
        return;
      }
      if (parsed < 0) {
        nextErrors[key] = 'Value cannot be negative.';
      }
    };

    validateRequiredPositive('loanAdvance', form.loanAdvance);
    validateOptionalNonNegative('establishmentFee', form.establishmentFee);
    validateOptionalNonNegative('brokerageFee', form.brokerageFee);
    validateOptionalNonNegative('legalCosts', form.legalCosts);
    validateOptionalNonNegative('titleInsurance', form.titleInsurance);
    validateOptionalNonNegative('valuationFee', form.valuationFee);
    validateOptionalNonNegative('stampDuty', form.stampDuty);
    validateOptionalNonNegative('otherDeductions', form.otherDeductions);

    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const input: any = {};
    for (const [k, v] of Object.entries(form)) {
      if (v !== '' && v != null) {
        input[k] = [
          'loanAdvance',
          'establishmentFee',
          'brokerageFee',
          'legalCosts',
          'titleInsurance',
          'valuationFee',
          'stampDuty',
          'otherDeductions',
        ].includes(k)
          ? parseFloat(v as string)
          : v;
      }
    }
    createSettlement.mutate(input, {
      onSuccess: () => setShowCreate(false),
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Settlement Statements</h3>
        <Button
          variant="primary"
          onClick={() => setShowCreate(!showCreate)}
          className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-white text-sm rounded-md hover:"
        >
          <Plus className="w-3.5 h-3.5" /> New Statement
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3"
        >
          <p className="text-sm font-medium text-gray-700">Create Settlement Statement</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: 'loanAdvance', label: 'Loan Advance *' },
              { key: 'establishmentFee', label: 'Establishment Fee' },
              { key: 'brokerageFee', label: 'Brokerage Fee' },
              { key: 'legalCosts', label: 'Legal Costs' },
              { key: 'titleInsurance', label: 'Title Insurance' },
              { key: 'valuationFee', label: 'Valuation Fee' },
              { key: 'stampDuty', label: 'Stamp Duty' },
              { key: 'otherDeductions', label: 'Other Deductions' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                <input
                  type="number"
                  step="0.01"
                  min={key === 'loanAdvance' ? '0.01' : '0'}
                  value={(form as any)[key]}
                  onChange={e => {
                    const sanitized = e.target.value.replace(/[^0-9.]/g, '');
                    setForm({ ...form, [key]: sanitized });
                    setFormErrors(prev => ({ ...prev, [key]: '' }));
                  }}
                  onBlur={() => {
                    const raw = String((form as any)[key] ?? '');
                    if (!raw.trim()) {
                      setFormErrors(prev => ({
                        ...prev,
                        [key]: key === 'loanAdvance' ? 'This field is required.' : '',
                      }));
                      return;
                    }

                    const parsed = Number(raw);
                    if (!Number.isFinite(parsed)) {
                      setFormErrors(prev => ({ ...prev, [key]: 'Enter a valid number.' }));
                      return;
                    }

                    if (key === 'loanAdvance' ? parsed <= 0 : parsed < 0) {
                      setFormErrors(prev => ({
                        ...prev,
                        [key]:
                          key === 'loanAdvance'
                            ? 'Value must be greater than 0.'
                            : 'Value cannot be negative.',
                      }));
                    }
                  }}
                  className={`w-full px-2 py-1.5 border rounded text-sm ${
                    formErrors[key] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  required={key === 'loanAdvance'}
                />
                {formErrors[key] && <p className="mt-1 text-xs text-red-600">{formErrors[key]}</p>}
              </div>
            ))}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Notes</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
              placeholder="Settlement notes..."
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="primary"
              type="submit"
              disabled={createSettlement.isPending}
              className="px-3 py-1.5 text-white text-sm rounded hover: disabled:opacity-50"
            >
              {createSettlement.isPending ? 'Creating…' : 'Create Statement'}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setShowCreate(false)}
              className="w-fit"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Statements list */}
      {settlements && settlements.length > 0 ? (
        <div className="space-y-3">
          {settlements.map((s: SettlementStatement) => (
            <div key={s.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-mono font-semibold text-gray-900">
                    {s.statementNumber}
                  </span>
                  <span
                    className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[s.status] || 'bg-gray-100'}`}
                  >
                    {s.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {fmtDate(s.statementDate || s.createdAt)}
                  </span>
                  {s.status === 'DRAFT' && (
                    <button
                      onClick={() => reconcile.mutate({ sid: s.id })}
                      disabled={reconcile.isPending}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Reconcile
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-xs">
                <div>
                  <span className="text-gray-500">Loan Advance</span>
                  <p className="font-semibold text-gray-900">{fmt(s.loanAdvance)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Total Deductions</span>
                  <p className="font-semibold text-red-600">{fmt(s.totalDeductions)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Net Advance</span>
                  <p className="font-semibold text-green-700">{fmt(s.netAdvance)}</p>
                </div>
                {s.variance && parseFloat(s.variance) !== 0 && (
                  <div>
                    <span className="text-gray-500">Variance</span>
                    <p
                      className={`font-semibold ${parseFloat(s.variance) > 0 ? 'text-amber-600' : 'text-red-600'}`}
                    >
                      {fmt(s.variance)}
                    </p>
                  </div>
                )}
                {s.reconciledAt && (
                  <div>
                    <span className="text-gray-500">Reconciled</span>
                    <p className="font-medium text-green-700">{fmtDate(s.reconciledAt)}</p>
                  </div>
                )}
              </div>

              {s.reconciliationNotes && (
                <p className="text-xs text-gray-500 mt-2 italic">{s.reconciliationNotes}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 text-gray-400">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No settlement statements yet</p>
        </div>
      )}
    </div>
  );
}
